import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Paints the document canvas to match the area you're in.
 *
 * The app shell already paints itself, and on most screens that is the whole
 * story. It isn't at the edges of an installed iPhone. The status bar here is
 * opaque and space-reserving rather than the overlay `black-translucent` is
 * supposed to give (see the note in public/index.html) — so the strip it sits
 * in is outside the viewport the shell is laid out in, and iOS fills it with
 * the *canvas* colour, which comes from `body`. The same goes for the band
 * kept clear for the home indicator, for the rubber-band overscroll at either
 * end, and for the gutters beside the shell on anything wider than a phone.
 *
 * Every one of those was bone paper on the finance tab: a dark screen in a
 * pale frame. Nobody noticed before because every other screen in the app is
 * bone paper, so the canvas already agreed with the page by accident.
 *
 * Mounted once at the router, not in AppShell, because it has to cover the
 * full-screen booking forms too — those are mounted outside the shell and are
 * just as dark.
 */

type Area = "paper" | "vault";

const areaFor = (pathname: string): Area =>
  pathname.startsWith("/finance") ? "vault" : "paper";

const AreaChrome = () => {
  const { pathname } = useLocation();
  const area = areaFor(pathname);

  // Layout effect, not a plain one: this runs before the browser paints, so
  // arriving straight onto a finance URL never shows a frame of the pale
  // frame before it corrects itself.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-area", area);

    // The colour is read back out of CSS rather than written twice. index.css
    // owns both tokens; duplicating either as a string here is how the two
    // drift apart later.
    const colour = getComputedStyle(root).getPropertyValue(`--color-${area}`).trim();
    if (!colour) return;

    // Set inline, because index.html gives `body` an inline background of its
    // own so the first paint isn't white — and a stylesheet rule loses to
    // that no matter how specific it is. Inline against inline, last write
    // wins, and no `!important` anywhere.
    root.style.backgroundColor = colour;
    document.body.style.backgroundColor = colour;
  }, [area]);

  return null;
};

export default AreaChrome;
